import React, { useEffect, useState, Fragment } from 'react'
import { Button, Card, Dropdown, Typography, Alert, Tooltip, Modal, InputNumber, Tag, Select, Flex, Radio } from 'antd'
import { ExclamationCircleOutlined, EditOutlined, QuestionCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { format, getDayOfYear, getDate, getMonth, isWithinInterval, addYears, isAfter, isBefore, isEqual, isSameYear } from 'date-fns'

import getDateInUserTimezone from '@vacationtracker/shared/functions/get-date-in-user-timezone'
import getValidityStart from '@vacationtracker/shared/functions/get-validity-start'
import getvalidityStartForQuota from '@vacationtracker/shared/functions/get-validity-start-for-senioirty-enitlement'

import UserLeaveTypeForm from '../user-leave-type-form'
import QuotaBox from '../quota-box'
import QuotaBoxLock from '../quota-box-lock'

import { AccrualTypeEnum, ShortestLeaveIntervalEnum } from '@vacationtracker/shared/types/leave-policy'
import { LeaveDisplayFormatEnum, QuotaDisplayFormatEnum } from '../../types/display-in-days-hours'
import { IUserLeaveQuotas } from './types'
import { IUserLeaveTypeResult, IUserQuota, IUserLocationLeavePolicies, IMyProfileQuota, IMyProfileLeaveType } from '../../../frontend/src/types/custom-queries'
import FormattedDate from '../formatted-date'
import RolloverExpiry from './rollover-expiry'
import { getFormattedWorkhours, getQuotaInUnitsIfSet, getRawOrFormattedQuota, getRawQuotaInSettingsUnit } from '../../functions/get-days-or-hours'
import { getLeaveDaysNumber } from '../utils/leave-quotas'
import { InitialBroughtForwardTooltip } from './quota-tooltips'
import { sumQuotaAmounts } from './calculate-quota-box-numbers'
import { getRawQuotaDaysOrHours } from '../../functions/quotas'
import { AccruedTooltip } from './accrued-tooltip'
import { getAverageWorkingHours, getUserWorkingHoursDependingOnType, userHasWorkingHoursSchedule } from '../../functions/work-week'
import { IUserLeaveTypesUpdateEvent } from '../../types/leave-type'

const { Text, Paragraph } = Typography

const UserLeaveQuotas = ({
  userId,
  quotas,
  amIAdmin,
  onQuotaUpdate,
  IntlMessages,
  location,
  userName,
  setActiveTab,
  hourlyLeaveAccounting,
  locale,
  userStartDate,
  userStatus,
}: IUserLeaveQuotas): React.ReactElement => {
  const { formatMessage } = useIntl()
  const [selectedQuotaPeriod, setSelectedQuotaPeriod] = useState<IUserQuota | IMyProfileQuota>(quotas[quotas.length - 1])
  const [currentPeriod, setCurrentPeriod] = useState<IUserQuota | IMyProfileQuota>(quotas[0])
  const [dateRanges, setDateRanges] = useState<(IUserQuota & { leaveDayName: string })[]>([])
  const [selectedDateRangeName, setSelectedDateRangeName] = useState<string>('')
  const [visibleUserEditQuotaForm, setVisibleUserEditQuotaForm] = useState<boolean>(false)
  const [selectedLeaveType, setSelectedLeaveType] = useState<IUserLeaveTypeResult | IMyProfileLeaveType>(() => {
    return quotas[quotas.length - 1].leaveTypes.filter((lt) => lt.isActive).sort((a, b) => (a.position < b.position ? -1 : 1))[0]
  })
  const [isAccrualInfoVisible, setIsAccrualInfoVisible] = useState(false)
  const [selectedLeavePolicy, setSelectedLeavePolicy] = useState<IUserLocationLeavePolicies>()
  const [isInitialPeriod, setIsInitialPeriod] = useState(false)
  const [rolloverQuotas, setRolloverQuotas] = useState(0)
  const [isCurrentYearSelected, setIsCurrentYearSelected] = useState(true)
  const [isNextYearSelected, setIsNextYearSelected] = useState(false)
  const [showInitialBroughtForwardModal, setShowInitialBroughtForwardModal] = useState(false)
  const [displayFormat, setDisplayFormat] = useState<QuotaDisplayFormatEnum>(QuotaDisplayFormatEnum.formatted)
  const initalBroughtForward = selectedLeaveType
    ? getLeaveDaysNumber(selectedLeaveType.accrualType, selectedLeaveType.allowHalfDays, selectedLeaveType?.shortestLeaveInterval, rolloverQuotas, hourlyLeaveAccounting)
    : 0

  const setQuotas = (allQuotas: IUserQuota[]) => {
    setDateRanges(
      allQuotas
        .map((quota, index) => {
          let leaveDayRangeName: string
          if (isWithinInterval(Date.now(), { start: new Date(quota.yearStart), end: new Date(quota.yearEnd) })) {
            leaveDayRangeName = formatYearDisplay('app.currentYear', quota.yearStart, quota.yearEnd)
            setSelectedDateRangeName(leaveDayRangeName)
            setSelectedQuotaPeriod(quota)
            if (index === 0) {
              setIsInitialPeriod(true)
            }
            if (quota.leaveTypes.length > 0 && selectedLeaveType) {
              const leavePolicy = location.leavePolicies.find((lp) => lp.leaveType.id === selectedLeaveType.leaveTypeId)
              if (leavePolicy) {
                setSelectedLeavePolicy(leavePolicy)
              }
            }
            setCurrentPeriod(quota)
          } else if (isWithinInterval(addYears(Date.now(), 1), { start: new Date(quota.yearStart), end: new Date(quota.yearEnd) })) {
            leaveDayRangeName = formatYearDisplay('app.nextYear', quota.yearStart, quota.yearEnd)
          } else if (
            // We can't check only is year start 1. January, becasue location can have short year. Ex. from 1. Jan to 1. Jul
            getDayOfYear(getDateInUserTimezone(quota.yearStart)) === 1 &&
            getDate(getDateInUserTimezone(quota.yearEnd)) === 31 &&
            getMonth(getDateInUserTimezone(quota.yearEnd)) === 11
          ) {
            leaveDayRangeName = format(getDateInUserTimezone(quota.yearStart), 'yyyy')
          } else if (format(getDateInUserTimezone(quota.yearStart), 'yyyy') === format(getDateInUserTimezone(quota.yearEnd), 'yyyy')) {
            leaveDayRangeName = `${format(getDateInUserTimezone(quota.yearStart), 'MMM dd')} - ${format(getDateInUserTimezone(quota.yearEnd), 'MMM dd, yyyy')}`
          } else {
            leaveDayRangeName = `${format(getDateInUserTimezone(quota.yearStart), 'MMM dd, yyyy')} - ${format(getDateInUserTimezone(quota.yearEnd), 'MMM dd, yyyy')}`
          }

          return {
            ...quota,
            leaveDayName: leaveDayRangeName,
          }
        })
        .filter(Boolean)
    )
  }

  useEffect(() => {
    const filteredQuotas = quotas.map((quota) => {
      return {
        ...quota,
        leaveTypes: quota.leaveTypes.filter((lt) => lt.isActive).sort((a, b) => (a.position < b.position ? -1 : 1)),
      }
    })
    setQuotas(filteredQuotas)
  }, [quotas])

  useEffect(() => {
    const previouslySelectedLeaveType = selectedLeaveType && selectedQuotaPeriod.leaveTypes.find((lt) => lt.leaveTypeId === selectedLeaveType.leaveTypeId)
    if (previouslySelectedLeaveType) {
      setSelectedLeaveType(previouslySelectedLeaveType)
    } else {
      setSelectedLeaveType(selectedQuotaPeriod.leaveTypes[0])
    }
  }, [selectedQuotaPeriod])

  useEffect(() => {
    if (selectedLeaveType) {
      setRolloverQuotas(getRawQuotaInSettingsUnit(selectedLeaveType?.quotas?.rolloverFromPreviousYear, hourlyLeaveAccounting))
    }
  }, [selectedLeaveType])

  const selectLeaveType = (id: string | number): void => {
    const quota = selectedQuotaPeriod.leaveTypes.find((type) => type.leaveTypeId === id)
    if (quota) {
      setSelectedLeaveType(quota)
      if (selectedQuotaPeriod.leaveTypes.length > 0) {
        const leavePolicy = location.leavePolicies.find((lp) => lp.leaveType.id === quota.leaveTypeId)

        if (leavePolicy) {
          setSelectedLeavePolicy(leavePolicy)
        }
      }
    }
  }

  const formatYearDisplay = (message: string, yearStart: string, yearEnd: string) => {
    const universalYearStart = getDateInUserTimezone(yearStart)
    const universalYearEnd = getDateInUserTimezone(yearEnd)
    return `${formatMessage({
      id: message,
    })} (${format(
      new Date(universalYearStart),
      `${isSameYear(new Date(universalYearStart), new Date(universalYearEnd)) ? ' MMM dd' : ' MMM dd, yyyy'}`
    )} - ${format(new Date(universalYearEnd), 'MMM dd, yyyy')} )`
  }

  const handleYearChange = (event) => {
    const userLeaveType = dateRanges[event.key]
    userLeaveType.leaveTypes.sort((a, b) => (a.position < b.position ? -1 : 1))

    const hasLeaveTypeInSelectedQuotaPeriod = userLeaveType.leaveTypes.find((type) => type.leaveTypeId === selectedLeaveType?.leaveTypeId)
    if (!hasLeaveTypeInSelectedQuotaPeriod) {
      setSelectedLeaveType(userLeaveType.leaveTypes[0])
      const leavePolicy = location.leavePolicies.find((lp) => lp.leaveType.id === userLeaveType.leaveTypes[0]?.leaveTypeId)

      if (leavePolicy) {
        setSelectedLeavePolicy(leavePolicy)
      }
    }
    setSelectedQuotaPeriod(userLeaveType)
    setIsInitialPeriod(() => {
      if (userLeaveType.yearStart === quotas[0].yearStart && userLeaveType.yearEnd === quotas[0].yearEnd) {
        return true
      } else {
        return false
      }
    })
    setSelectedDateRangeName(userLeaveType.leaveDayName)

    setIsCurrentYearSelected(false)
    setIsNextYearSelected(false)
    if (isWithinInterval(Date.now(), { start: new Date(userLeaveType.yearStart), end: new Date(userLeaveType.yearEnd) })) {
      setIsCurrentYearSelected(true)
    } else if (isWithinInterval(addYears(Date.now(), 1), { start: new Date(userLeaveType.yearStart), end: new Date(userLeaveType.yearEnd) })) {
      setIsNextYearSelected(true)
    }
  }

  const handleUserQuotaUpdate = async (data: IUserLeaveTypesUpdateEvent) => {
    const validityStart = getValidityStart(selectedQuotaPeriod as IUserQuota, currentPeriod as IUserQuota)
    const validityStartForQuota = getvalidityStartForQuota(selectedQuotaPeriod as IUserQuota, currentPeriod as IUserQuota)

    const proratedLeaveTypes: string[] = data.shouldProrate
      ? (Array.from(new Set([...(selectedLeaveType.userProratedLeaveTypes as string[]), data.leaveTypeId])) as string[])
      : (selectedLeaveType?.userProratedLeaveTypes?.filter((leaveType) => leaveType !== data.leaveTypeId) as string[])

    const updates = {
      eventType: 'USER_LEAVE_TYPES_UPDATED',
      eventGroup: 'USER',
      userId,
      leaveTypeId: data.leaveTypeId,
      daysPerYear: data.quotaInUnits,
      ...(data.seniorityEntitlementForYear !== undefined && {
        seniorityEntitlementDaysForYear:
          data.unit === 'hours'
            ? data.seniorityEntitlementForYear / getAverageWorkingHours(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [])
            : data.seniorityEntitlementForYear,
        seniorityEntitlementHoursForYear:
          data.unit === 'hours'
            ? data.seniorityEntitlementForYear
            : data.seniorityEntitlementForYear * getAverageWorkingHours(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || []),
      }),
      ...(data.entitlementByRoleForYear !== undefined && {
        entitlementByRoleDaysForYear:
          data.unit === 'hours'
            ? data.entitlementByRoleForYear / getAverageWorkingHours(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [])
            : data.entitlementByRoleForYear,
        entitlementByRoleHoursForYear:
          data.unit === 'hours'
            ? data.entitlementByRoleForYear
            : data.entitlementByRoleForYear * getAverageWorkingHours(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || []),
      }),
      hasUnlimitedDays: Boolean(data.hasUnlimitedDays),
      currentYearOnly: !data.applyToFutureYears,
      ignoreRolloverFromPreviousYear: data.ignoreRolloverFromPreviousYear,
      shouldProrate: isInitialPeriod ? proratedLeaveTypes?.length > 0 : undefined,
      proratedLeaveTypes: isInitialPeriod ? proratedLeaveTypes : undefined,
      // TODO: This event should start using #timestamp in SK (as seniority uses it) and get rid of this thing below
      // We use the date of the migration as a validity start date for the migrated teams,
      // as that was at the end of the year, we need to be creative here.
      // Instead of using this hour, minute, second and milliseconds in the selected period,
      // We put everything in the last day of the fiscal year, but we use "percentage" of the year progress to diff the dates
      // But the base for "percentages" is 3600000 so we can set minutes (60), seconds (60) and milliseconds (1000).
      // For example, 50% of the year will produce: 00:30:00:000, etc.
      validityStart,
      // For editing seniority entitlement in currentYear we use the current time
      // For editing seniority entitlement in the past we use the endDate of selectedQuotaPeriod and added in SK timestamp
      // To get senioirty entitlement we sort by validityDate and if have two or more events with same validityDate we sort by timestamp and take the last one
      validityStartForQuota,
      quotaSetIn: data.unit,
      hoursPerYear: 0,
    }
    if (hourlyLeaveAccounting) {
      const workingHours = getAverageWorkingHours(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [])
      // In case of hourly leave accounting, we need to pass the number of hours as "hoursPerDay"
      // and to calculate the number of days per year by dividing the number of hours by the average working hours per day
      ;((updates.daysPerYear = data.quotaInUnits / workingHours), (updates.hoursPerYear = data.quotaInUnits))
    }
    if (data.hasUnlimitedDays) {
      updates.daysPerYear = 0
      updates.hoursPerYear = 0
      updates.quotaSetIn = hourlyLeaveAccounting ? 'hours' : 'days'
    }

    setIsInitialPeriod(false)

    if (onQuotaUpdate) {
      await onQuotaUpdate(updates)
    }

    setVisibleUserEditQuotaForm(false)
  }

  const handleUserRolloverQuotaUpdate = async () => {
    const validityStart = getValidityStart(quotas[0] as IUserQuota, currentPeriod as IUserQuota)
    const workingHours = getAverageWorkingHours(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [])

    const updates = {
      eventType: 'USER_LEAVE_TYPES_ROLLOVER_UPDATED',
      eventGroup: 'USER',
      userId,
      leaveTypeId: selectedLeaveType.leaveTypeId,
      initialRollover: true,
      customRolloverDays: hourlyLeaveAccounting ? rolloverQuotas / workingHours : rolloverQuotas,
      customRolloverHours: hourlyLeaveAccounting ? rolloverQuotas : rolloverQuotas * workingHours,
      quotaSetIn: hourlyLeaveAccounting ? 'hours' : 'days',
      validityStart: validityStart,
    }

    setIsInitialPeriod(false)

    if (onQuotaUpdate) {
      await onQuotaUpdate(updates)
    }
    setShowInitialBroughtForwardModal(false)
  }

  const handleGoToUserLogPage = () => {
    setIsAccrualInfoVisible(false)
    if (setActiveTab) {
      setActiveTab({ key: 'logs' })
    }
  }

  const getExpiredRolloverFooter = (rolloverExpiryDate: string) => {
    if (rolloverExpiryDate === '') {
      return ''
    }

    let isExpired = false
    if (isAfter(new Date(), new Date(rolloverExpiryDate)) && !isEqual(new Date(), new Date(rolloverExpiryDate))) {
      isExpired = true
    }

    if (!isExpired) {
      return ''
    }

    return (
      <Paragraph>
        <IntlMessages
          id="components.userLeaveQuotas.expiredDateInfo"
          values={{
            date: <FormattedDate value={rolloverExpiryDate?.slice(0, 10)} format="MMMM Do YYYY." locale={locale} />,
          }}
        />
      </Paragraph>
    )
  }

  const getQuotaTitle = (): JSX.Element => {
    if (selectedLeaveType?.accrualType !== AccrualTypeEnum.none && selectedLeavePolicy?.firstEarningDate) {
      return (
        <>
          {hourlyLeaveAccounting ? <IntlMessages id="components.userLeaveQuotas.accruedHours" /> : <IntlMessages id="components.userLeaveQuotas.accruedDays" />}
          <Tooltip
            open={isAccrualInfoVisible}
            title={
              <AccruedTooltip
                selectedLeaveType={selectedLeaveType}
                userName={userName}
                policyFirstEarningDate={selectedLeavePolicy?.firstEarningDate}
                handleGoToUserLogPage={handleGoToUserLogPage}
                formatMessage={formatMessage}
                IntlMessages={IntlMessages}
                // Since we have many cases to cover if semi-monthly could be any day of the month,
                // we checked our database and found that, so far, all users either use the 1st or the 16th of the month.
                // So, we decided to hardcode this part so that the period start is always either the 1st or the 16th of the month.
                yearStartDay={selectedLeavePolicy && selectedLeavePolicy.accrualType === AccrualTypeEnum.semiMonthly ? 1 : (location.yearStartDay as number)}
                accrualPeriodStart={selectedLeavePolicy?.accrualPeriodStart as string}
                isCurrentYearSelected={isCurrentYearSelected}
                hourlyLeaveAccounting={hourlyLeaveAccounting}
                userLocale={locale}
                userStatus={userStatus}
                capHit={Boolean(selectedLeaveType?.capHit)}
                workingHours={getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || []}
              />
            }
            onOpenChange={() => setIsAccrualInfoVisible(!isAccrualInfoVisible)}
          >
            <QuestionCircleOutlined style={{ marginLeft: '10px' }} />
          </Tooltip>
        </>
      )
    } else {
      return <IntlMessages id="app.quota" />
    }
  }

  const getQuotaFooter = (selectedLeaveType: IUserLeaveTypeResult | IMyProfileLeaveType): JSX.Element => {
    if (selectedLeaveType?.accrualType !== AccrualTypeEnum.none) {
      return (
        <Paragraph data-testid="quota-default-days-per-year">
          <IntlMessages id="app.of" />
          &nbsp;
          {getRawOrFormattedQuota(
            selectedLeaveType.quotas.quotaAtYearEnd,
            formatMessage,
            userHasWorkingHoursSchedule(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || []) || displayFormat === QuotaDisplayFormatEnum.raw,
            hourlyLeaveAccounting ? 'hours' : 'days',
            LeaveDisplayFormatEnum.short
          )}
          {amIAdmin && <EditOutlined style={{ color: '#7f00ff', marginLeft: 3 }} onClick={() => setVisibleUserEditQuotaForm(true)} />}
        </Paragraph>
      )
    } else if (amIAdmin) {
      return (
        <Button type="link" key="change-quota" onClick={() => setVisibleUserEditQuotaForm(true)} disabled={selectedQuotaPeriod.leaveTypes.length === 0}>
          <IntlMessages id="components.userLeaveQuotas.editQuotaButton" />
        </Button>
      )
    } else {
      return <></>
    }
  }

  // For NONE accrual policies TOIL (net of lapsed) is already inside the quota box, so only accrual policies
  // get their own TOIL box and move lapsed TOIL into the expired box
  const isAccrualPolicy = Boolean(selectedLeaveType?.accrualType && selectedLeaveType.accrualType !== AccrualTypeEnum.none)
  const showsToilInOwnBox = Boolean(isAccrualPolicy && !selectedLeaveType?.hasUnlimitedDays && selectedLeaveType?.quotas)
  // Gross, not net: the lapsed part is subtracted again in the expired box, so netting it here would double-count it
  const showToilBox = Boolean(showsToilInOwnBox && getRawQuotaDaysOrHours(hourlyLeaveAccounting, selectedLeaveType.quotas.toil) > 0)
  // Gated exactly like the TOIL box: both appear together or the row stops adding up
  const lapsedToil = showsToilInOwnBox ? selectedLeaveType.quotas.toilLapsed : undefined
  const hasLapsedToil = Boolean(lapsedToil && getRawQuotaDaysOrHours(hourlyLeaveAccounting, lapsedToil) > 0)
  const expiredWithLapsedToil =
    selectedLeaveType?.quotas && lapsedToil && hasLapsedToil
      ? sumQuotaAmounts({
          amounts: [selectedLeaveType.quotas.expired, lapsedToil],
          hourlyLeaveAccounting,
          workingHours: getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [],
          shortestLeaveInterval: selectedLeaveType.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay,
        })
      : selectedLeaveType?.quotas?.expired
  // If rollover never expires the backend returns an empty string or a date 1000 years out,
  // so a brought forward expiry only counts as real when it is set and within the next 5 years
  const hasBroughtForwardExpiry = selectedLeaveType?.rolloverExpiryDate !== '' && isBefore(new Date(selectedLeaveType?.rolloverExpiryDate), addYears(new Date(), 5))
  const showExpiredBox = hasLapsedToil || hasBroughtForwardExpiry

  return (
    <>
      <Card
        title={<IntlMessages id="components.userLeaveQuotas.sectionTitle" />}
        className="user-leave-quotas"
        style={{ marginBottom: 30 }}
        styles={{ body: { padding: 8 } }}
        extra={[
          <Fragment key="menu">
            <IntlMessages id="components.userLeaveQuotas.selectPeriod" />
            <Dropdown
              menu={{
                onClick: handleYearChange,
                items: dateRanges.map((lt, index: number) => ({
                  key: index,
                  label: lt.leaveDayName,
                })),
              }}
            >
              <Button style={{ minWidth: 220, marginLeft: 5, textAlign: 'left' }}>{selectedDateRangeName}</Button>
            </Dropdown>
          </Fragment>,
        ]}
      >
        {selectedQuotaPeriod.leaveTypes.length === 0 && !selectedLeaveType && (
          <Alert
            message={
              <>
                <ExclamationCircleOutlined style={{ color: 'red' }} /> <IntlMessages id="components.userLeaveQuotas.leavePoliciesNotExistInfo" />
              </>
            }
            type="error"
            style={{ margin: 25 }}
          />
        )}
        {selectedLeaveType && (
          <div>
            <Text>
              <Flex className="leave-type-selector-quota-box" wrap justify="space-between" align="center" gap={12} style={{ padding: '0 16px', marginBottom: 16 }}>
                <div className="leave-type-selector-item">
                  <IntlMessages id="app.leaveType" />:
                  <Select
                    style={{ marginLeft: 10, width: 250 }}
                    options={selectedQuotaPeriod.leaveTypes.map((type) => ({
                      value: type.leaveTypeId,
                      label: type.leaveTypeName,
                    }))}
                    onChange={(value) => selectLeaveType(value)}
                    value={selectedLeaveType?.leaveTypeId}
                  ></Select>
                </div>
                <div>
                  <IntlMessages id="components.userLeaveQuotas.displayIn" />
                  :&nbsp;
                  <Radio.Group
                    value={displayFormat}
                    onChange={(e) => setDisplayFormat(e.target.value === 'raw' ? QuotaDisplayFormatEnum.raw : QuotaDisplayFormatEnum.formatted)}
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="formatted">
                      <IntlMessages
                        id={
                          userHasWorkingHoursSchedule(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [])
                            ? 'components.userLeaveQuotas.display.formattedHoursAndMinutes'
                            : 'components.userLeaveQuotas.display.formatted'
                        }
                      />
                    </Radio.Button>
                    <Radio.Button value="raw">
                      <IntlMessages id={hourlyLeaveAccounting ? 'components.userLeaveQuotas.display.raw.hours' : 'components.userLeaveQuotas.display.raw.days'} />
                    </Radio.Button>
                  </Radio.Group>
                </div>
              </Flex>
              <Flex justify="space-between" align="center" style={{ padding: '0 16px', marginBottom: 24 }}>
                <span>
                  <IntlMessages id="app.workday" />
                  :&nbsp;
                  <Tag style={{ fontWeight: 'bold' }} color="purple">
                    {getFormattedWorkhours(formatMessage, getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [], location.firstDayOfWeek)}
                  </Tag>
                </span>
              </Flex>
            </Text>
            <div className="quota-box-container">
              {!selectedLeaveType?.hasUnlimitedDays && (
                <>
                  {isInitialPeriod && amIAdmin && (
                    <>
                      <QuotaBoxLock
                        isLocked={!selectedLeavePolicy?.maxRolloverDays}
                        title={
                          <>
                            <IntlMessages id="components.userLeaveQuotas.initialBroughtForward" />
                            <InitialBroughtForwardTooltip
                              value={getRawQuotaInSettingsUnit(selectedLeaveType?.quotas?.rolloverFromPreviousYear, hourlyLeaveAccounting)}
                              units={hourlyLeaveAccounting ? 'hours' : 'days'}
                            />
                          </>
                        }
                        lockedTooltip={
                          <IntlMessages
                            id="components.userLeaveQuotas.unlockBroughtForwardInfo"
                            values={{ leaveTypeName: selectedLeaveType.leaveTypeName, locationName: location.name }}
                          />
                        }
                        dataTestId={'quota-brought-forward-days'}
                        value={selectedLeaveType?.quotas?.rolloverFromPreviousYear}
                        valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                        footer={
                          <Button type="link" key="change-quota" onClick={() => setShowInitialBroughtForwardModal(true)}>
                            <IntlMessages id="app.change" />
                          </Button>
                        }
                        displayFormat={displayFormat}
                      />
                      <div className="quota-formula">
                        <Text type="secondary" strong>
                          +
                        </Text>
                      </div>
                    </>
                  )}
                  {isInitialPeriod && !amIAdmin && (
                    <>
                      <QuotaBox
                        title={
                          <>
                            <IntlMessages id="components.userLeaveQuotas.initialBroughtForward" />
                            <InitialBroughtForwardTooltip
                              value={getRawQuotaInSettingsUnit(selectedLeaveType?.quotas?.rolloverFromPreviousYear, hourlyLeaveAccounting)}
                              units={hourlyLeaveAccounting ? 'hours' : 'days'}
                            />
                          </>
                        }
                        doNotShowDisplayInDaysOrHoursTooltip
                        dataTestId="quota-brought-forward-days"
                        value={selectedLeaveType?.quotas?.rolloverFromPreviousYear}
                        quotas={selectedLeaveType?.quotas}
                        hasUnlimitedDays={selectedLeaveType.hasUnlimitedDays}
                        valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                        footer={''}
                        displayFormat={displayFormat}
                        shortestLeaveInterval={selectedLeaveType?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay}
                      />
                      <div className="quota-formula">
                        <Text type="secondary" strong>
                          +
                        </Text>
                      </div>
                    </>
                  )}
                  {!isInitialPeriod && (selectedLeavePolicy?.maxRolloverDays !== 0 || selectedLeaveType?.rolloverFromPreviousYear !== 0) && (
                    <>
                      <QuotaBox
                        title={<IntlMessages id="app.broughtForward" />}
                        dataTestId={'quota-brought-forward-days'}
                        value={selectedLeaveType?.quotas?.rolloverFromPreviousYear}
                        valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                        footer={
                          <RolloverExpiry
                            IntlMessages={IntlMessages}
                            locale={locale}
                            isCurrentYearSelected={isCurrentYearSelected}
                            yearEndDate={typeof selectedQuotaPeriod.yearEnd === 'string' ? new Date(selectedQuotaPeriod.yearEnd) : selectedQuotaPeriod.yearEnd}
                            rolloverExpiryDate={selectedLeaveType.rolloverExpiryDate}
                            isNextYearSelected={isNextYearSelected}
                            maxRolloverDays={selectedLeavePolicy?.maxRolloverDays || 0}
                          />
                        }
                        quotas={selectedLeaveType?.quotas}
                        hasUnlimitedDays={selectedLeaveType.hasUnlimitedDays}
                        displayFormat={displayFormat}
                        leaveTypeName={selectedLeaveType.leaveTypeName}
                        locationName={location.name}
                        shortestLeaveInterval={selectedLeaveType?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay}
                        broughtForwardLimit={(() => {
                          const maxRolloverDays = selectedLeavePolicy?.maxRolloverDays as number
                          const maxRolloverHours = selectedLeavePolicy?.maxRolloverHours as number
                          const quotaSetIn = selectedLeavePolicy?.quotaSetIn

                          // If quotaSetIn is 'days' (or undefined for older companies) but hourlyLeaveAccounting is true, convert days to hours
                          // Use actual user working hours instead of default 8 hours
                          if ((quotaSetIn === 'days' || !quotaSetIn) && hourlyLeaveAccounting) {
                            const workingHours = getAverageWorkingHours(getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || [])
                            return maxRolloverDays * workingHours
                          }

                          return getQuotaInUnitsIfSet(maxRolloverDays, maxRolloverHours, hourlyLeaveAccounting, quotaSetIn)
                        })()}
                      />
                      <div className="quota-formula">
                        <Text type="secondary" strong>
                          +
                        </Text>
                      </div>
                    </>
                  )}
                </>
              )}
              <QuotaBox
                title={getQuotaTitle()}
                dataTestId={'quota-value'}
                value={selectedLeaveType?.quotas.earnedWithoutRollover}
                valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                leaveTypeName={selectedLeaveType.leaveTypeName}
                quotas={selectedLeaveType?.quotas}
                hasUnlimitedDays={selectedLeaveType.hasUnlimitedDays}
                footer={getQuotaFooter(selectedLeaveType)}
                displayFormat={displayFormat}
                doNotShowDisplayInDaysOrHoursTooltip={Boolean(selectedLeaveType?.accrualType !== AccrualTypeEnum.none && selectedLeavePolicy?.firstEarningDate)}
                broughtForwardLimit={
                  getQuotaInUnitsIfSet(
                    selectedLeavePolicy?.maxRolloverDays as number,
                    selectedLeavePolicy?.maxRolloverHours as number,
                    hourlyLeaveAccounting,
                    selectedLeavePolicy?.quotaSetIn
                  ) || 0
                }
                shortestLeaveInterval={selectedLeaveType?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay}
              />
              {showToilBox && (
                <>
                  <div className="quota-formula" data-testid="formula-toil">
                    <Text type="secondary" strong>
                      +
                    </Text>
                  </div>
                  <QuotaBox
                    title={<IntlMessages id="components.userLeaveQuotas.toil" />}
                    dataTestId={'quota-toil'}
                    value={selectedLeaveType.quotas.toil}
                    valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                    footer={''}
                    quotas={selectedLeaveType.quotas}
                    hasUnlimitedDays={false}
                    displayFormat={displayFormat}
                    shortestLeaveInterval={selectedLeaveType?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay}
                  />
                </>
              )}
              {showExpiredBox && (
                <>
                  <div className="quota-formula" data-testid="formula">
                    <Text type="secondary" strong>
                      -
                    </Text>
                  </div>
                  <QuotaBox
                    title={<IntlMessages id="app.expired" />}
                    dataTestId={'quota-expired'}
                    value={expiredWithLapsedToil || selectedLeaveType.quotas.expired}
                    valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                    footer={getExpiredRolloverFooter(selectedLeaveType.rolloverExpiryDate)}
                    quotas={selectedLeaveType?.quotas}
                    hasUnlimitedDays={selectedLeaveType.hasUnlimitedDays}
                    displayFormat={displayFormat}
                    locale={locale}
                    expirationDate={hasBroughtForwardExpiry ? selectedLeaveType.rolloverExpiryDate : undefined}
                    lapsedToil={hasLapsedToil ? lapsedToil : undefined}
                    shortestLeaveInterval={selectedLeaveType?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay}
                  />
                </>
              )}
              <div className="quota-formula">
                <Text type="secondary" strong>
                  -
                </Text>
              </div>
              <QuotaBox
                title={<IntlMessages id="app.used" />}
                dataTestId={'quota-used-days'}
                value={selectedLeaveType?.quotas?.used}
                scheduledDays={selectedLeaveType?.quotas?.scheduled}
                footer={''}
                valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                quotas={selectedLeaveType?.quotas}
                hasUnlimitedDays={false} // we show used quotas even when policy has unlimited days
                displayFormat={displayFormat}
                leaveTypeName={selectedLeaveType.leaveTypeName}
                shortestLeaveInterval={selectedLeaveType?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay}
              />
              <div className="quota-formula">
                <Text type="secondary" strong>
                  =
                </Text>
              </div>
              <QuotaBox
                title={<IntlMessages id="components.userLeaveQuotas.remaining" />}
                dataTestId={'quota-remaining-days'}
                value={selectedLeaveType?.quotas?.remaining}
                valueUnits={hourlyLeaveAccounting ? 'hours' : 'days'}
                footer={''}
                quotas={selectedLeaveType?.quotas}
                hasUnlimitedDays={selectedLeaveType.hasUnlimitedDays}
                displayFormat={displayFormat}
                broughtForwardLimit={getQuotaInUnitsIfSet(
                  selectedLeavePolicy?.maxRolloverDays as number,
                  selectedLeavePolicy?.maxRolloverHours as number,
                  hourlyLeaveAccounting,
                  selectedLeavePolicy?.quotaSetIn
                )}
                shortestLeaveInterval={selectedLeaveType?.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay}
              />
            </div>
            {selectedLeaveType && selectedLeaveType.capHit && (
              <Alert
                message={<IntlMessages id="components.leavePolicyForm.accrualCapWarning2" values={{ leaveTypeName: selectedLeaveType.leaveTypeName }} />}
                type="warning"
                showIcon
                closable
              />
            )}
            {selectedLeaveType && selectedLeaveType.pauseAccrualsWhenUserIsDeactivated && userStatus !== 'ACTIVE' && (
              <Alert message={<IntlMessages id="components.leavePolicyForm.pauseAccrualsWhenUserIsDeactivatedUserInfo" />} type="warning" showIcon />
            )}
          </div>
        )}
      </Card>
      {visibleUserEditQuotaForm && selectedLeaveType && (
        <UserLeaveTypeForm
          onSave={handleUserQuotaUpdate}
          handleCancel={() => {
            setVisibleUserEditQuotaForm(false)
          }}
          visible={visibleUserEditQuotaForm}
          leaveType={selectedLeaveType}
          IntlMessages={IntlMessages}
          dateRange={{
            name: selectedDateRangeName,
          }}
          hourlyLeaveAccounting={hourlyLeaveAccounting}
          userStartDate={userStartDate}
          locationResetQuotas={location.resetQuotas}
          userName={userName}
          workingHours={getUserWorkingHoursDependingOnType(selectedQuotaPeriod.userWorkingHours) || []}
          period={{
            start: selectedQuotaPeriod.yearStart as string,
            end: selectedQuotaPeriod.yearEnd as string,
          }}
          locale={locale}
          isInitialPeriod={isInitialPeriod}
        />
      )}
      {selectedLeaveType && (
        <Modal
          title={<IntlMessages id="components.userLeaveQuotas.editInitialQuotaTitle" values={{ leaveTypeName: selectedLeaveType.leaveTypeName }} />}
          open={showInitialBroughtForwardModal}
          closeIcon={<CloseCircleOutlined />}
          styles={{ body: { padding: 12 } }}
          okText={<IntlMessages id="app.save" />}
          onCancel={() => {
            setShowInitialBroughtForwardModal(false)
            setRolloverQuotas(getRawQuotaInSettingsUnit(selectedLeaveType?.quotas?.rolloverFromPreviousYear, hourlyLeaveAccounting))
          }}
          onOk={handleUserRolloverQuotaUpdate}
        >
          <div>
            <IntlMessages id="components.userLeaveQuotas.editInitialQuotaTooltip" />
          </div>
          <div style={{ marginTop: '30px' }}>
            <IntlMessages id="components.userLeaveQuotas.initialBroughtForward" />
            &nbsp;
            <InputNumber
              value={initalBroughtForward}
              onChange={(val) => {
                if (val !== null) {
                  setRolloverQuotas(val)
                }
              }}
            />
            &nbsp;
            <span className="quota-box-hours" data-testid={'quota-brought-forward-days'}>
              {hourlyLeaveAccounting && selectedLeavePolicy?.maxRolloverDays ? formatMessage({ id: 'app.hours' }) : formatMessage({ id: 'app.days' })}
            </span>
          </div>
        </Modal>
      )}
    </>
  )
}

export default UserLeaveQuotas
